/**
 * Deleting and restoring records.
 *
 * Every delete a person can make in the admin app comes through `softDelete`:
 * it stamps `deletedAt` and leaves the row — and any file it owns — in place,
 * so `restoreRecord` can bring it back until cleanup-deleted-records purges it
 * PURGE_AFTER_DAYS later. Nothing here removes a row. The rules, and the few
 * deletes that are legitimately permanent, are in
 * .claude/skills/soft-delete/SKILL.md.
 *
 * Kept out of api.js for the same reason fleet.js is: it is its own concern,
 * and every area of the app calls it.
 */

import { databases, DATABASE_ID, Query, getCurrentUser } from './appwrite';
import {
  SOFT_DELETE_TABLES,
  canDelete,
  canUpdate,
  isDeleted,
  isSoftDeleteTable,
  naturalKeyOf,
  notDeleted,
  parentOf,
} from '../lib/records';

function assertSoftDeleteTable(table) {
  if (!isSoftDeleteTable(table)) {
    // A table without deletedAt would silently accept nothing — Appwrite rejects
    // the unknown attribute — or, worse, somebody would reach for a hard delete.
    throw new Error(`${table} does not soft-delete; add deletedAt to it first (see the soft-delete skill)`);
  }
}

/** A row from its id, or the row itself when the caller already has it. */
async function rowOf(table, rowOrId) {
  if (rowOrId && typeof rowOrId === 'object' && rowOrId.$id && rowOrId.$permissions) return rowOrId;
  const id = typeof rowOrId === 'object' ? rowOrId?.$id || rowOrId?.id : rowOrId;
  if (!id) throw new Error('Nothing to delete');
  return databases.getDocument(DATABASE_ID, table, id);
}

const label = (table) => (SOFT_DELETE_TABLES[table]?.label || 'record').toLowerCase();

/**
 * Delete a record the way the app deletes: hidden everywhere at once, back with
 * one click for 90 days.
 *
 * Refused unless the user holds the row's own `delete` permission. Appwrite
 * sees this as an update, so it would otherwise let any member delete what only
 * the owner could — see canDelete in lib/records.js.
 */
export async function softDelete(table, rowOrId) {
  assertSoftDeleteTable(table);
  const [row, user] = await Promise.all([rowOf(table, rowOrId), getCurrentUser()]);
  if (isDeleted(row)) return row;
  if (!canDelete(row, user)) {
    throw new Error(`You don't have permission to delete this ${label(table)}`);
  }
  return databases.updateDocument(DATABASE_ID, table, row.$id, {
    deletedAt: new Date().toISOString(),
  });
}

/**
 * Bring a deleted record back.
 *
 * Refused when the record it belongs to is itself deleted — a restored photo on
 * a deleted request would appear nowhere — and when a live row already holds
 * its natural key, such as a newer override of the same standard line.
 */
export async function restoreRecord(table, rowOrId) {
  assertSoftDeleteTable(table);
  const [row, user] = await Promise.all([rowOf(table, rowOrId), getCurrentUser()]);
  if (!isDeleted(row)) return row;
  if (!canUpdate(row, user)) {
    throw new Error(`You don't have permission to restore this ${label(table)}`);
  }

  const parent = parentOf(table, row);
  if (parent && isSoftDeleteTable(parent.table)) {
    const parentRow = await databases.getDocument(DATABASE_ID, parent.table, parent.id).catch(() => null);
    if (!parentRow) {
      throw new Error(`The ${label(parent.table)} this belonged to no longer exists`);
    }
    if (isDeleted(parentRow)) {
      throw new Error(`Restore the ${label(parent.table)} first`);
    }
  }

  const key = naturalKeyOf(table, row);
  if (key) {
    const clash = await databases.listDocuments(DATABASE_ID, table, [
      ...Object.entries(key).map(([field, value]) => Query.equal(field, value)),
      notDeleted(),
      Query.limit(1),
    ]);
    if (clash.documents.length > 0) {
      throw new Error(`This ${label(table)} has been replaced since it was deleted — remove the newer one first`);
    }
  }

  return databases.updateDocument(DATABASE_ID, table, row.$id, { deletedAt: null });
}

const recordsApi = { softDelete, restoreRecord };
export default recordsApi;
